import { Form, Formik } from 'formik';

import ContractHeaderItem from '@/components/common/ContractHeaderItem/ContractHeaderInputItem';
import { GetContractDetails } from '@/graphql/contracts/_generated_/getContractDetails.query';
import styles from './parent-headers.module.scss';
import ContractDropdownHeaderItem from '@/components/common/ContractInputHeaderItem/ContractDropdownHeaderItem';
import { Pill } from '@/components/common/Pill/Pill';
import useGetCustomersLookUp from '../../hooks/useGetCustomersLookUp.query';
import { CustomerLookupOutput } from '@/graphql/contracts/_generated_/contracts';
import useUpdateDivertCustomer from '../../hooks/useUpdateDivertCustomer';
import { validationSchema } from './validationSchema';
// eslint-disable-next-line import/no-named-as-default
import ValidationAlert from '@/components/common/ValidationAlert/ValidationAlert';

interface IHeadersProps {
  isLoadingContractDetails?: boolean;
  contract: GetContractDetails['contract'] | undefined;
}

enum InvoicingParty {
  Owner = 'Owner',
  Divert = 'Divert',
  Parent = 'Parent'
}

export default function ParentHeaders({
  isLoadingContractDetails = false,
  contract
}: IHeadersProps) {
  const { isCustomersLookUpLoading, customers, getCustomersLookUp } = useGetCustomersLookUp();
  const { updateDivertCustomer } = useUpdateDivertCustomer();
  const invoicingParty = contract?.divertCustomer ? InvoicingParty.Divert :
    contract?.billToParent ? InvoicingParty.Parent : InvoicingParty.Owner;
  const invoicingTo = invoicingParty === InvoicingParty.Divert ?
    contract?.divertCustomer : invoicingParty === InvoicingParty.Parent ?
      contract?.customer.parentCustomer : contract?.customer;

  return (
    <>
      <Formik
        initialValues={{
          divertPaymentName: contract?.divertCustomer,
          divertPaymentAccountNumber: contract?.divertCustomer,
          currentCustomer: contract?.customer
        }}
        enableReinitialize={true}
        onSubmit={() => {
          return; //handling submit in a diff way
        }}
        validationSchema={validationSchema}
        validateOnMount={true}
      >
        {({ errors, touched, values, setFieldValue, setFieldTouched }) => (
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            width: contract?.billToParent || isLoadingContractDetails ? '650px' : '860px'
          }}>
            <div>
              <div className={styles['contract-header-container']}>
                <ContractHeaderItem
                  isLoading={isLoadingContractDetails}
                  label="Parent Account Name"
                  value={contract?.customer.parentCustomer?.customerName}
                  disabled
                  inputStyle={{ width: '100%' }}
                />
              </div>
              <div className={styles['contract-header-container']}>
                <ContractHeaderItem
                  isLoading={isLoadingContractDetails}
                  label="Parent Account Code"
                  value={contract?.customer.parentCustomer?.customerAccountNumber}
                  disabled
                  inputStyle={{ width: '100%' }}
                />
              </div>
            </div>
            <div>
              <div className={styles['contract-header-container']}>
                <ContractHeaderItem
                  isLoading={isLoadingContractDetails}
                  label="Account Name"
                  value={contract?.customer?.customerName}
                  disabled
                  inputStyle={{ width: '100%' }}
                />
              </div>

              <div className={styles['contract-header-container']}>
                <ContractHeaderItem
                  isLoading={isLoadingContractDetails}
                  label="Account Code"
                  value={contract?.customer?.customerAccountNumber}
                  disabled
                  inputStyle={{ width: '100%' }}
                />
              </div>
            </div>
            {contract && !contract.billToParent && (<Form className={styles['contract-header-container']}>
              <div className={styles['contract-header-container']}>
                <ContractDropdownHeaderItem<CustomerLookupOutput>
                  disabled
                  label="Divert Account Name"
                  name="divertPaymentName"
                  labelClassName={styles['contract-label']}
                  getOptionLabel={(option) => option.customerName!}
                  getOptionValue={(option) => option.customerName!}
                  selectedOption={values.divertPaymentName}
                  handleInputChange={(value) => getCustomersLookUp(value)}
                  optionsData={customers}
                  handleChange={(option) => {
                    const customerOrParentSelected = option?.customerID === contract?.customer?.customerID ||
                      contract?.customer?.parentCustomer?.customerID &&
                      option?.customerID === contract?.customer?.parentCustomer?.customerID;
                    setFieldValue('divertPaymentName', option);
                    setFieldTouched('divertPaymentName', true, false);
                    if (contract?.contractID && !customerOrParentSelected && !errors.divertPaymentName) {
                      updateDivertCustomer({
                        contractId: contract?.contractID,
                        divertCustomerId: option?.customerID
                      });
                    }
                  }}
                  isLoading={isCustomersLookUpLoading}
                  isLoadingContractDetails={isLoadingContractDetails}
                  isSearchable={true}
                  isClearable={true}
                />
                {errors.divertPaymentName && touched.divertPaymentName && (
                  <ValidationAlert isFloating={false}>{errors.divertPaymentName}</ValidationAlert>
                )}
              </div>
              <div className={styles['contract-header-container']}>
                <ContractDropdownHeaderItem<CustomerLookupOutput>
                  disabled
                  label="Divert Account Code"
                  labelClassName={styles['contract-label']}
                  selectedOption={values.divertPaymentAccountNumber}
                  name="divertPaymentAccountNumber"
                  getOptionLabel={(option) => option.customerAccountNumber}
                  getOptionValue={(option) => option.customerAccountNumber}
                  handleInputChange={(value) => getCustomersLookUp(value)}
                  optionsData={customers}
                  handleChange={(option) => {
                    const customerOrParentSelected = option?.customerID === contract?.customer?.customerID ||
                      contract?.customer?.parentCustomer?.customerID &&
                      option?.customerID === contract?.customer?.parentCustomer?.customerID;
                    setFieldValue('divertPaymentAccountNumber', option);
                    setFieldTouched('divertPaymentAccountNumber', true, false);
                    if (contract?.contractID && !customerOrParentSelected && !errors.divertPaymentAccountNumber) {
                      updateDivertCustomer({
                        contractId: contract?.contractID,
                        divertCustomerId: option?.customerID
                      });
                    }
                  }}
                  isLoading={isCustomersLookUpLoading}
                  isLoadingContractDetails={isLoadingContractDetails}
                  isSearchable={true}
                  isClearable={true}
                />
                {errors.divertPaymentAccountNumber && touched.divertPaymentAccountNumber && (
                  <ValidationAlert isFloating={false}>
                    {errors.divertPaymentAccountNumber}
                  </ValidationAlert>
                )}
              </div>
            </Form>)}
            <div>
                <>
                    <div className={styles['contract-header-invoicing']}>
                    <div >Invoicing To</div>
                    <Pill
                      name={invoicingParty}
                      title={invoicingParty}
                      size="large"
                      className="m-2"
                    >
                      {invoicingParty}
                    </Pill>
                  </div>
                  <div className={styles['contract-header-container']}>
                    <ContractHeaderItem
                      isLoading={isLoadingContractDetails}
                      label="Invoice Account Name"
                      value={invoicingTo?.customerName}
                      disabled
                      inputStyle={{ width: '100%' }}
                    />
                  </div>
                  <div className={styles['contract-header-container']}>
                    <ContractHeaderItem
                      isLoading={isLoadingContractDetails}
                      label="Invoice Account Code"
                      value={invoicingTo?.customerAccountNumber}
                      disabled
                      inputStyle={{ width: '100%' }}
                    />
                  </div>
                </>
            </div>
          </div >
        )}
      </Formik >
    </>
  );
}


